import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { fieldErrorsFrom422, formatApiErrorDetail } from "@/lib/api";
import { FormField, adminInput } from "./shared";

const BLANK = { name: "", description: "", price: "", billing: "one_time", visible: true };

export const PackageDialog = ({ open, item, onClose, onSave }) => {
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setForm(item ? { ...BLANK, ...item, price: String(item.price) } : BLANK); setErrors({}); } }, [open, item]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    const price = Number(form.price);
    if (form.price === "" || Number.isNaN(price) || price <= 0) errs.price = "Price must be a positive amount";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await onSave({ name: form.name.trim(), description: form.description, price, billing: form.billing, visible: form.visible });
    } catch (err) {
      const fe = fieldErrorsFrom422(err.response?.data?.detail);
      setErrors(Object.keys(fe).length ? fe : { form: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="package-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto border-white/10 bg-[#0D111A]">
        <DialogHeader><DialogTitle className="text-white">{item ? "Edit package" : "New package"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <FormField id="package-name" label="Name" error={errors.name}>
            <input id="package-name" data-testid="package-input-name" value={form.name} onChange={set("name")} className={adminInput(errors.name)} />
          </FormField>
          <FormField id="package-description" label="Description">
            <textarea id="package-description" rows={3} data-testid="package-input-description" value={form.description} onChange={set("description")} className={adminInput(false)} />
          </FormField>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField id="package-price" label="Price (USD)" error={errors.price}>
              <input id="package-price" type="number" min="0" step="0.01" data-testid="package-input-price" value={form.price} onChange={set("price")} className={adminInput(errors.price)} />
            </FormField>
            <FormField id="package-billing" label="Billing">
              <select id="package-billing" data-testid="package-input-billing" value={form.billing} onChange={set("billing")} className={adminInput(false)}>
                <option value="one_time">One-time</option>
                <option value="monthly">Monthly</option>
              </select>
            </FormField>
          </div>
          <label className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-3 text-sm text-slate-300">
            Visible on Services page
            <Switch checked={form.visible} onCheckedChange={(v) => setForm({ ...form, visible: v })} data-testid="package-input-visible" />
          </label>
          {errors.form && <p className="text-sm text-red-400">{errors.form}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} data-testid="package-cancel-button" className="rounded-full border border-white/10 px-5 py-2 text-sm text-slate-300 hover:bg-white/5">Cancel</button>
            <button type="submit" disabled={busy} data-testid="package-save-button" className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
